import React, { useState, useCallback } from 'react'
import {
  BarChart3,
  Users,
  Eye,
  Globe,
  MonitorSpeaker,
  Smartphone,
  Tablet,
  TrendingUp,
  Link2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts'
import { useQuery } from 'react-query'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import LoadingSpinner from '../common/LoadingSpinner'
import { cn } from '../../utils/cn'

const RANGES = [
  { label: '7 days',  value: '7d'  },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
]

// ── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, subtitle, index = 0, isLoading, dimmed }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.08 }}
  >
    <Card className={cn(
      'hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-[#2f2b41] shadow-sm',
      dimmed
    )}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5 min-w-0">
            <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">{title}</p>
            {isLoading ? (
              <div className="animate-pulse bg-neutral-300 dark:bg-neutral-600 h-7 w-20 rounded" />
            ) : (
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
              </p>
            )}
            {subtitle && !isLoading && (
              <p className="text-xs text-neutral-400 dark:text-neutral-500">{subtitle}</p>
            )}
          </div>
          <div className="h-11 w-11 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg shrink-0">
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
)

// ── Page Views Chart ────────────────────────────────────────────────────────
const ViewsChart = React.memo(({ data }) => {
    const [tooltip, setTooltip] = useState(null)
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

    const handleMouseMove = useCallback((e) => {
      if (e?.activePayload?.length) setTooltip(e.activePayload)
    }, [])
    const handleMouseMoveCapture = useCallback((e) => {
      const wrapper = e.currentTarget.querySelector('.recharts-wrapper')
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect()
        setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
      }
    }, [])

    if (!data?.length) {
      return (
        <div className="h-56 flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500 gap-2">
          <BarChart3 className="h-8 w-8 opacity-40" />
          <p className="text-sm">No visits recorded in this period</p>
        </div>
      )
    }

    return (
      <div className="relative" onMouseMove={handleMouseMoveCapture} onMouseLeave={() => setTooltip(null)}>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data} onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
            <defs>
              <linearGradient id="clientColorViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#6c63ff" stopOpacity={0.7} />
                <stop offset="95%" stopColor="#6c63ff" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-neutral-200 dark:text-neutral-700" />
            <XAxis dataKey="name" tick={{ fill: 'currentColor', fontSize: 11 }} className="text-neutral-500 dark:text-neutral-400" />
            <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} className="text-neutral-500 dark:text-neutral-400" allowDecimals={false} />
            <Area type="monotone" dataKey="views" stroke="#6c63ff" fill="url(#clientColorViews)" strokeWidth={2} name="Page Views" />
          </AreaChart>
        </ResponsiveContainer>
        {tooltip && (
          <div
            className="absolute z-50 bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 shadow-lg pointer-events-none"
            style={{ left: mousePos.x + 12, top: mousePos.y - 10, transform: mousePos.x > 400 ? 'translateX(-110%)' : 'none' }}
          >
            {tooltip.map((entry, i) => (
              <p key={i} className="text-sm flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-neutral-500 dark:text-neutral-400">{entry.name}:</span>
                <span className="font-semibold" style={{ color: entry.color }}>{entry.value.toLocaleString()}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    )
  })

// ── Device Chart ────────────────────────────────────────────────────────────
const DeviceChart = React.memo(({ deviceTypes }) => {
    const deviceIcons = { Desktop: MonitorSpeaker, Mobile: Smartphone, Tablet: Tablet }

    if (!deviceTypes?.length) {
      return <div className="h-32 flex items-center justify-center text-neutral-400 dark:text-neutral-500 text-sm">No device data yet</div>
    }

    return (
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <ResponsiveContainer width={140} height={140}>
          <PieChart>
            <Pie data={deviceTypes} cx="50%" cy="50%" innerRadius={38} outerRadius={62} paddingAngle={4} dataKey="value" stroke="none">
              {deviceTypes.map((entry, i) => <Cell key={i} fill={entry.color} stroke="none" />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2 w-full">
          {deviceTypes.map((d, i) => {
            const Icon = deviceIcons[d.name] || Globe
            return (
              <div key={i} className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: d.color }} />
                <span className="text-xs text-neutral-600 dark:text-neutral-400 w-16 shrink-0">{d.name}</span>
                <div className="flex-1 bg-neutral-100 dark:bg-dark-400 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${d.value}%`, backgroundColor: d.color }} />
                </div>
                <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300 w-8 text-right">{d.value}%</span>
              </div>
            )
          })}
        </div>
      </div>
    )
})

const ClientAnalytics = () => {
  const [range, setRange] = useState('30d')

  const { data: raw, isLoading, isFetching, isError, refetch } = useQuery(
    ['client-analytics', range],
    () => apiService.get(`/analytics/overview?range=${range}`).then(r => r.data),
    { staleTime: 120000, keepPreviousData: true, retry: 2 }
  )

  const data = raw || {}
  const {
    totalPageViews = 0,
    uniqueVisitors  = 0,
    activityData    = [],
    topPages        = [],
    deviceTypes     = [],
    referrerData    = [],
  } = data

  const dimmed = isFetching && !isLoading ? 'opacity-60' : ''

  // ── Error state ─────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <Card className="border-error-200 dark:border-error-800">
        <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
          <AlertCircle className="h-10 w-10 text-error-500" />
          <p className="text-neutral-600 dark:text-neutral-400">Failed to load analytics data.</p>
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">Analytics</h1>
          <p className={cn('text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-1.5', isFetching && !isLoading && 'text-accent-500')}>
            {isFetching && !isLoading
              ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Updating…</>
              : 'Track your website performance and visitor insights'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date range */}
          <div className="flex items-center gap-1 p-1 bg-neutral-100 dark:bg-dark-200 rounded-lg border border-neutral-200 dark:border-[#2f2b41]">
            {RANGES.map(r => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                  range === r.value
                    ? 'bg-white dark:bg-dark-100 text-accent-600 dark:text-accent-400 shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={refetch}
            disabled={isFetching}
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          title="Page Views"
          value={totalPageViews}
          icon={Eye}
          subtitle={`Last ${range === '7d' ? '7' : range === '30d' ? '30' : '90'} days`}
          index={0}
          isLoading={isLoading}
          dimmed={dimmed}
        />
        <StatCard
          title="Unique Visitors"
          value={uniqueVisitors}
          icon={Users}
          subtitle="Based on IP address"
          index={1}
          isLoading={isLoading}
          dimmed={dimmed}
        />
      </div>

      {/* Page Views Over Time */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
        <Card className={cn('border-neutral-200 dark:border-[#2f2b41]', dimmed)}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-accent-600 dark:text-accent-400" />
              Page Views Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading
              ? <div className="h-56 flex items-center justify-center"><LoadingSpinner size="lg" /></div>
              : <ViewsChart data={activityData} />
            }
          </CardContent>
        </Card>
      </motion.div>

      {/* Top Pages + Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Top Pages */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
          <Card className={cn('border-neutral-200 dark:border-[#2f2b41] h-full', dimmed)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                Top Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center gap-3">
                      <div className="w-6 h-6 rounded bg-neutral-200 dark:bg-neutral-700 shrink-0" />
                      <div className="flex-1 h-4 rounded bg-neutral-200 dark:bg-neutral-700" />
                      <div className="w-10 h-4 rounded bg-neutral-200 dark:bg-neutral-700" />
                    </div>
                  ))}
                </div>
              ) : topPages.length === 0 ? (
                <div className="py-10 text-center text-neutral-400 dark:text-neutral-500 text-sm">No page visits recorded yet</div>
              ) : (
                <div className="space-y-3">
                  {topPages.map((p, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-medium text-neutral-400 dark:text-neutral-500 w-4 shrink-0">{i + 1}</span>
                          <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate font-mono">{p.page || '/'}</span>
                        </div>
                        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 shrink-0">{p.views.toLocaleString()}</span>
                      </div>
                      <div className="ml-6 bg-neutral-100 dark:bg-dark-400 rounded-full h-1">
                        <div
                          className="h-1 rounded-full bg-accent-500 transition-all duration-500"
                          style={{ width: `${Math.round((p.views / topPages[0].views) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Traffic Sources + Devices */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}
          className="space-y-6">

          {/* Traffic Sources */}
          <Card className={cn('border-neutral-200 dark:border-[#2f2b41]', dimmed)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Link2 className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                Traffic Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse h-6 rounded bg-neutral-200 dark:bg-neutral-700" />)}
                </div>
              ) : referrerData.length === 0 ? (
                <div className="py-6 text-center text-neutral-400 dark:text-neutral-500 text-sm">No referrer data yet</div>
              ) : (
                <div className="space-y-2.5">
                  {referrerData.map((r, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-neutral-700 dark:text-neutral-300">{r.source}</span>
                        <span className="text-neutral-500 dark:text-neutral-400">{r.count.toLocaleString()} <span className="text-xs">({r.percentage}%)</span></span>
                      </div>
                      <div className="bg-neutral-100 dark:bg-dark-400 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(r.percentage, 100)}%`, backgroundColor: r.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Devices */}
          <Card className={cn('border-neutral-200 dark:border-[#2f2b41]', dimmed)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MonitorSpeaker className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                Devices
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading
                ? <div className="h-24 flex items-center justify-center"><LoadingSpinner /></div>
                : <DeviceChart deviceTypes={deviceTypes} />
              }
            </CardContent>
          </Card>

        </motion.div>
      </div>

    </div>
  )
}

export default ClientAnalytics
